import {Badge} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {IconGitCompare, IconHistory, IconPrinter} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo} from 'react';
import type {PersonnelBadgeSimpleResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import 'src/utilities/extensions/stringExtensions';

export interface BadgeHistoryTableProps {
  history: PersonnelBadgeSimpleResponse[];
  printingBadgeId?: number;
  compareBadgeId?: number;
  isLoading?: boolean;
  onPrint: (badgeId: number) => void;
  onCompare: (badgeId: number) => void;
}

// mantine-react-table + `useTable`/`useTableState`, matching the Instances
// "Badge Templates" list convention — per-personnel badge history is small
// client-side data (no server paging), so `manual*` is disabled.
export const BadgeHistoryTable: React.FC<BadgeHistoryTableProps> = ({
  history,
  printingBadgeId,
  compareBadgeId,
  isLoading = false,
  onPrint,
  onCompare,
}) => {
  const columns = useMemo<MRT_ColumnDef<PersonnelBadgeSimpleResponse>[]>(
    () => [
      {
        id: 'version',
        header: 'Version',
        accessorFn: row => row.version,
        Cell: ({row}) => (
          <Badge color="blue" variant="light">
            v{row.original.version}
          </Badge>
        ),
      },
      {
        id: 'generatedAt',
        header: 'Generated At',
        accessorFn: row => (row.generatedAt ? new Date(row.generatedAt) : null),
        Cell: ({row}) => <>{String(row.original.generatedAt).toLongDate()}</>,
      },
      {
        id: 'generatedBy',
        header: 'Generated By',
        accessorFn: row => row.generatedByName ?? 'System',
      },
      {
        id: 'template',
        header: 'Template',
        accessorFn: row =>
          row.badgeTemplateName
            ? `${row.badgeTemplateName} v${row.templateVersion}`
            : '—',
        Cell: ({row}) => (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm">
              {row.original.badgeTemplateName
                ? `${row.original.badgeTemplateName} v${row.original.templateVersion}`
                : '—'}
            </Text>
            {row.original.autoGenerated && (
              <Badge color="blue" variant="light" size="sm">
                Auto
              </Badge>
            )}
          </Group>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'personnel-badge-history-table',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 5,
    },
    defaultSorting: [],
    defaultShowColumnFilters: false,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: history,
      getRowId: row => String(row.id),
      enableGlobalFilter: false,
      enableColumnFilters: false,
      enableTopToolbar: false,
      mantineTableBodyRowProps: ({row}) => ({
        'data-testid': `badge-history-row-${row.original.id}`,
        bg: compareBadgeId === row.original.id ? 'blue.0' : undefined,
      }),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.Menu>
            <TableRowActions.MenuItem
              icon={<IconPrinter size={16} />}
              disabled={printingBadgeId === row.original.id}
              onClick={() => onPrint(row.original.id)}>
              {printingBadgeId === row.original.id ? 'Printing…' : 'Print'}
            </TableRowActions.MenuItem>
            <TableRowActions.MenuItem
              icon={<IconGitCompare size={16} />}
              onClick={() => onCompare(row.original.id)}>
              Compare
            </TableRowActions.MenuItem>
          </TableRowActions.Menu>
        </TableRowActions>
      ),
    },
    totalCount: history.length,
    isError: false,
    isLoading: false,
    isFetching: false,
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (history.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <IconHistory size={24} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">
            No badges have been generated for this person yet.
          </Text>
        </Stack>
      </Center>
    );
  }

  return <div data-testid="badge-history-table">{table}</div>;
};
